import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  formatLocalDob,
  getDaysInMonth,
  getMaxAdultDobString,
  getMinDobString,
  getMinimumAgeForGender,
  MAX_HUMAN_AGE,
  parseLocalDob,
  validateAdultDob,
} from '../../utils/dobUtils';

export interface DateOfBirthInputProps {
  value: string;
  onChange: (dob: string, age: number | null) => void;
  gender?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | null;
  variant?: 'light' | 'dark';
  id?: string;
  className?: string;
}

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export const DateOfBirthInput: React.FC<DateOfBirthInputProps> = ({
  value,
  onChange,
  gender,
  label = 'Date of Birth (Female 18+ / Male 21+)',
  required = true,
  disabled = false,
  error = null,
  variant = 'light',
  id = 'dob-input',
  className = '',
}) => {
  const nativeDateRef = useRef<HTMLInputElement | null>(null);
  const parsed = useMemo(() => parseLocalDob(value), [value]);

  const [selectedYear, setSelectedYear] = useState<string>(parsed ? String(parsed.year) : '');
  const [selectedMonth, setSelectedMonth] = useState<string>(parsed ? String(parsed.month) : '');
  const [selectedDay, setSelectedDay] = useState<string>(parsed ? String(parsed.day) : '');

  useEffect(() => {
    const nextParsed = parseLocalDob(value);
    if (nextParsed) {
      setSelectedYear(String(nextParsed.year));
      setSelectedMonth(String(nextParsed.month));
      setSelectedDay(String(nextParsed.day));
    } else if (!value) {
      setSelectedYear('');
      setSelectedMonth('');
      setSelectedDay('');
    }
  }, [value]);

  const minDob = useMemo(() => getMinDobString(), []);
  const maxDob = useMemo(() => getMaxAdultDobString(), []);
  const minRequiredAge = useMemo(() => getMinimumAgeForGender(gender), [gender]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const maxSelectableYear = currentYear - 14;
    const minAdultYear = currentYear - MAX_HUMAN_AGE;
    const years: number[] = [];
    for (let y = maxSelectableYear; y >= minAdultYear; y -= 1) {
      years.push(y);
    }
    return years;
  }, []);

  const maxDaysInCurrentSelection = useMemo(() => {
    const y = Number(selectedYear) || 2000;
    const m = Number(selectedMonth) || 1;
    return getDaysInMonth(y, m);
  }, [selectedYear, selectedMonth]);

  const dayOptions = useMemo(() => {
    const days: number[] = [];
    for (let d = 1; d <= maxDaysInCurrentSelection; d += 1) {
      days.push(d);
    }
    return days;
  }, [maxDaysInCurrentSelection]);

  const emitIfComplete = (yStr: string, mStr: string, dStr: string) => {
    const y = Number(yStr);
    const m = Number(mStr);
    let d = Number(dStr);
    if (Number.isInteger(y) && y > 0 && Number.isInteger(m) && m >= 1 && m <= 12 && Number.isInteger(d) && d >= 1) {
      const maxD = getDaysInMonth(y, m);
      if (d > maxD) {
        d = maxD;
        setSelectedDay(String(d));
      }
      const formatted = formatLocalDob(y, m, d);
      const validation = validateAdultDob(formatted, new Date(), gender);
      onChange(formatted, validation.age);
    } else if (!yStr && !mStr && !dStr) {
      onChange('', null);
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextYear = e.target.value;
    setSelectedYear(nextYear);
    const nextMonth = selectedMonth || '1';
    const nextDay = selectedDay || '1';
    if (nextYear && !selectedMonth) setSelectedMonth('1');
    if (nextYear && !selectedDay) setSelectedDay('1');
    emitIfComplete(nextYear, nextYear ? nextMonth : selectedMonth, nextYear ? nextDay : selectedDay);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextMonth = e.target.value;
    setSelectedMonth(nextMonth);
    emitIfComplete(selectedYear, nextMonth, selectedDay);
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextDay = e.target.value;
    setSelectedDay(nextDay);
    emitIfComplete(selectedYear, selectedMonth, nextDay);
  };

  const handleNativeDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (!raw) {
      setSelectedYear('');
      setSelectedMonth('');
      setSelectedDay('');
      onChange('', null);
      return;
    }
    const validation = validateAdultDob(raw, new Date(), gender);
    const p = parseLocalDob(raw);
    if (p) {
      setSelectedYear(String(p.year));
      setSelectedMonth(String(p.month));
      setSelectedDay(String(p.day));
    }
    onChange(raw, validation.age);
  };

  const openNativePicker = () => {
    if (!nativeDateRef.current || disabled) return;
    try {
      if (typeof (nativeDateRef.current as any).showPicker === 'function') {
        (nativeDateRef.current as any).showPicker();
      } else {
        nativeDateRef.current.focus();
        nativeDateRef.current.click();
      }
    } catch {
      nativeDateRef.current.focus();
    }
  };

  const validation = useMemo(() => (value ? validateAdultDob(value, new Date(), gender) : null), [value, gender]);
  const displayError = error || (validation && !validation.valid ? validation.error : null);

  const isDark = variant === 'dark';
  const labelClass = isDark
    ? 'block text-sm font-medium text-slate-300 mb-1.5'
    : 'block text-xs font-bold text-slate-700 mb-1';
  const selectClass = isDark
    ? `w-full bg-slate-950 border ${
        displayError ? 'border-red-500/80' : 'border-slate-800 focus:border-violet-500'
      } rounded-xl px-2.5 py-2.5 text-white text-sm focus:outline-none transition-colors`
    : `w-full bg-slate-50 border ${
        displayError ? 'border-rose-400 bg-rose-50/30' : 'border-slate-200 focus:border-indigo-600'
      } rounded-xl px-2.5 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all`;

  const nativeInputClass = isDark
    ? `w-full bg-slate-950 border ${
        displayError ? 'border-red-500/80' : 'border-slate-800 focus:border-violet-500'
      } rounded-xl pl-9 pr-3 py-2 text-white text-xs focus:outline-none transition-colors`
    : `w-full bg-slate-50 border ${
        displayError ? 'border-rose-400 bg-rose-50/30' : 'border-slate-200 focus:border-indigo-600'
      } rounded-xl pl-9 pr-3 py-2 text-slate-700 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all`;

  return (
    <div className={className}>
      {label && (
        <div className="flex items-center justify-between mb-1">
          <label htmlFor={id} className={labelClass}>
            {label} {required && <span className="text-rose-500">*</span>}
          </label>
          {validation?.valid && validation.age !== null && (
            <span
              className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                isDark
                  ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              }`}
            >
              <CheckCircle2 className="w-3 h-3" />
              {validation.age} yrs ({minRequiredAge}+ Verified)
            </span>
          )}
        </div>
      )}

      {/* Touch & WebView Friendly Day / Month / Year Selectors */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div>
          <select
            aria-label="Birth Day"
            value={selectedDay}
            onChange={handleDayChange}
            disabled={disabled}
            className={selectClass}
          >
            <option value="">Day</option>
            {dayOptions.map((d) => (
              <option key={d} value={String(d)}>
                {String(d).padStart(2, '0')}
              </option>
            ))}
          </select>
        </div>

        <div>
          <select
            aria-label="Birth Month"
            value={selectedMonth}
            onChange={handleMonthChange}
            disabled={disabled}
            className={selectClass}
          >
            <option value="">Month</option>
            {MONTH_OPTIONS.map((m) => (
              <option key={m.value} value={String(m.value)}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <select
            aria-label="Birth Year"
            value={selectedYear}
            onChange={handleYearChange}
            disabled={disabled}
            className={selectClass}
          >
            <option value="">Year</option>
            {yearOptions.map((y) => (
              <option key={y} value={String(y)}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Synchronized Native Calendar Input for Direct Date Picker Selection */}
      <div className="relative flex items-center">
        <button
          type="button"
          onClick={openNativePicker}
          disabled={disabled}
          title="Open Calendar Picker"
          className={`absolute left-2.5 p-0.5 rounded ${
            isDark ? 'text-slate-400 hover:text-violet-400' : 'text-slate-400 hover:text-indigo-600'
          }`}
        >
          <Calendar className="w-4 h-4" />
        </button>
        <input
          ref={nativeDateRef}
          id={id}
          type="date"
          value={value || ''}
          min={minDob}
          max={maxDob}
          required={required}
          disabled={disabled}
          onChange={handleNativeDateChange}
          onClick={openNativePicker}
          className={nativeInputClass}
        />
      </div>

      {displayError && (
        <p
          className={`mt-1.5 flex items-center gap-1 text-xs font-medium ${
            isDark ? 'text-red-400' : 'text-rose-600'
          }`}
        >
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>{displayError}</span>
        </p>
      )}
    </div>
  );
};
